import React, { useState, useEffect } from 'react';
import ArticlesAdmin from '../components/ArticlesAdmin';
import ExecutiveEditorsAdmin from '../components/ExecutiveEditorsAdmin';
import ResearchVerboseAdmin from '../components/ResearchVerboseAdmin';
import NavigationMenuAdmin from '../components/NavigationMenuAdmin';
import PageContentAdmin from '../components/PageContentAdmin';
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import AJHMLogo from '../components/AJHMLogo';
import ImageUploadField from '../components/ImageUploadField';
import { loadData, saveToCode, adminLogin, adminLogout } from '../services/codeDataStore';
import { DEFAULT_SUBMISSION_CHECKLIST } from '../data/submissionChecklist';
import {
  FaNewspaper,
  FaUsers,
  FaBook,
  FaCompass,
  FaFileAlt,
  FaBalanceScale,
  FaCog,
  FaChartLine,
  FaSignOutAlt,
  FaExternalLinkAlt,
  FaLock,
  FaCheckCircle,
  FaExclamationCircle
} from 'react-icons/fa';

const ADMIN_TABS = [
  { id: 'journals', label: 'Articles', icon: FaNewspaper, desc: 'Manuscripts & Research' },
  { id: 'editors', label: 'Editorial Board', icon: FaUsers, desc: 'Members & Categories' },
  { id: 'verbose', label: 'Issues & Volumes', icon: FaBook, desc: 'Volumes & Validity' },
  { id: 'page_contents', label: 'Live Page Contents', icon: FaFileAlt, desc: '8 Journal Core Pages' },
  { id: 'menu', label: 'Navigation Menu', icon: FaCompass, desc: 'Menu Hierarchy' },
  { id: 'legal', label: 'Legal Content', icon: FaBalanceScale, desc: 'Privacy, Copyright, Terms, Disclaimer' },
  { id: 'settings', label: 'Site Settings', icon: FaCog, desc: 'Branding & Mission' },
  { id: 'analytics', label: 'Analytics', icon: FaChartLine, desc: 'Traffic & Reads' },
];

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [activeTab, setActiveTab] = useState('journals');

  const [loggingIn, setLoggingIn] = useState(false);

  // Site Settings & Legal State
  const [miscData, setMiscData] = useState({
    heroTitle: '',
    heroTagline: '',
    heroImg: '',
    ourMission: '',
    Topics: '',
    submissionChecklist: DEFAULT_SUBMISSION_CHECKLIST.join('\n'),
    privacyPolicy: '',
    copyright: '',
    termsOfService: '',
    disclaimer: ''
  });
  const [miscLoading, setMiscLoading] = useState(false);
  const [miscSuccess, setMiscSuccess] = useState('');
  const [miscError, setMiscError] = useState('');

  // Active sub-tab inside Legal Content
  const [activeLegalTab, setActiveLegalTab] = useState('privacy'); // 'privacy' | 'copyright' | 'terms' | 'disclaimer'

  useEffect(() => {
    if (isAuthenticated) {
      fetchMiscData();
    }
  }, [isAuthenticated]);

  // Authentication (Firebase email/password user)
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      await adminLogin(username, password);
      setIsAuthenticated(true);
      setLoginError('');
    } catch (err) {
      setLoginError(err.message || 'Invalid username or password.');
    }
    setLoggingIn(false);
  };

  const handleLogout = () => {
    adminLogout().catch(() => {});
    setIsAuthenticated(false);
    setUsername('');
    setPassword('');
  };

  // Fetch misc and legal data
  const fetchMiscData = async () => {
    setMiscLoading(true);
    try {
      const d = await loadData('site', null);
      if (d) {
        setMiscData((prev) => ({
          ...prev,
          heroTitle: d.heroTitle || '',
          heroTagline: d.heroTagline || '',
          heroImg: d.heroImg || '',
          ourMission: d.ourMission || '',
          Topics: d.Topics || '',
          submissionChecklist: d.submissionChecklist || DEFAULT_SUBMISSION_CHECKLIST.join('\n'),
          privacyPolicy: d.privacyPolicy || '',
          copyright: d.copyright || '',
          termsOfService: d.termsOfService || '',
          disclaimer: d.disclaimer || ''
        }));
      }
    } catch (error) {
      console.error('Error fetching misc data:', error);
      setMiscError('Error fetching legal / settings data');
    }
    setMiscLoading(false);
  };

  const handleMiscChange = (e) => {
    const { name, value } = e.target;
    setMiscData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMiscSave = async (e) => {
    e.preventDefault();
    setMiscLoading(true);
    setMiscError('');
    setMiscSuccess('');
    try {
      const current = await loadData('site', {});
      await saveToCode('site', { ...(current || {}), ...miscData });
      setMiscSuccess('Saved! It is live now.');
      setTimeout(() => setMiscSuccess(''), 3500);
    } catch (error) {
      console.error('Error saving settings:', error);
      setMiscError(error.message || 'Failed to save content');
    }
    setMiscLoading(false);
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#071E3D] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 font-sans">
        <div className="max-w-md w-full bg-white rounded-3xl p-8 sm:p-10 shadow-2xl space-y-6">
          <div className="text-center space-y-2">
            <div className="inline-block">
              <AJHMLogo height={58} className="h-14 max-h-14" />
            </div>
            <h2 className="text-2xl font-serif font-extrabold text-[#0B3B60]">
              Journal Admin Portal
            </h2>
            <p className="text-xs text-gray-500">
              Asian Journal of Medical and Health Sciences
            </p>
          </div>

          {loginError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-xs flex items-center gap-2">
              <FaExclamationCircle className="text-base flex-shrink-0" />
              <span>{loginError}</span>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-1">
              <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                Username
              </label>
              <input
                type="text"
                required
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Admin username"
                className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
              />
            </div>

            <div className="space-y-1">
              <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                Password
              </label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••••"
                className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
              />
            </div>

            <button
              type="submit"
              disabled={loggingIn}
              className="w-full bg-[#03478E] hover:bg-[#023366] disabled:opacity-60 text-white py-3 rounded-xl text-xs font-bold uppercase tracking-wider shadow-md transition-all flex items-center justify-center gap-2"
            >
              <FaLock className="text-xs" />
              <span>{loggingIn ? 'Signing In...' : 'Sign In to Dashboard'}</span>
            </button>
          </form>

          <p className="text-[11px] text-gray-400 text-center">
            Authorized editorial staff and journal administrators only.
          </p>
        </div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-[#FAF9F6] text-gray-800 font-sans pb-16">
      
      {/* Top Navbar */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between gap-4">
          
          {/* Logo & Portal Title */}
          <div className="flex items-center gap-3">
            <AJHMLogo height={46} className="h-11 max-h-11 flex-shrink-0" />
            <div className="hidden sm:block border-l border-gray-200 pl-3">
              <h1 className="text-sm font-bold text-[#0B3B60] leading-none">
                AIMJHM Administration Suite
              </h1>
              <p className="text-[10px] text-gray-400 font-mono mt-0.5">
                Live Editorial Management
              </p>
            </div>
          </div>

          {/* Right Header Actions */}
          <div className="flex items-center gap-3">
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-semibold transition"
            >
              <FaExternalLinkAlt className="text-[10px]" />
              <span className="hidden sm:inline">View Live Site</span>
            </a>

            <div className="hidden md:flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200 font-semibold">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              <span>Online as Admin</span>
            </div>

            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-red-50 hover:bg-red-600 hover:text-white text-red-600 rounded-xl text-xs font-bold transition shadow-2xs"
            >
              <FaSignOutAlt className="text-xs" />
              <span>Logout</span>
            </button>
          </div>

        </div>

        {/* Tab Switcher Bar */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 overflow-x-auto scrollbar-none border-t border-gray-100">
          <div className="flex items-center gap-2 py-2.5 min-w-max">
            {ADMIN_TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                    isActive
                      ? 'bg-[#03478E] text-white shadow-xs'
                      : 'bg-white hover:bg-gray-100 text-gray-700 border border-gray-200'
                  }`}
                >
                  <Icon className={`text-xs ${isActive ? 'text-blue-200' : 'text-[#03478E]'}`} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </header>

      {/* Main Content Body */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Where saves go */}
        <div className="mb-6 bg-blue-50 border border-blue-200 text-[#0B3B60] px-4 py-3 rounded-xl text-xs flex items-start gap-2">
          <FaCheckCircle className="text-sm flex-shrink-0 mt-0.5 text-[#03478E]" />
          <span>Saves go to Firebase and are live for everyone right away.</span>
        </div>

        {/* TAB 1: ARTICLES & JOURNALS */}
        {activeTab === 'journals' && <ArticlesAdmin />}

        {/* TAB 2: EDITORIAL BOARD */}
        {activeTab === 'editors' && <ExecutiveEditorsAdmin />}

        {/* TAB 3: ISSUES & VOLUMES */}
        {activeTab === 'verbose' && <ResearchVerboseAdmin />}

        {/* TAB 4: LIVE PAGE CONTENTS */}
        {activeTab === 'page_contents' && <PageContentAdmin onSwitchTab={setActiveTab} />}

        {/* TAB 5: NAVIGATION MENU */}
        {activeTab === 'menu' && <NavigationMenuAdmin />}

        {/* TAB 6: LEGAL CONTENT (PRIVACY, COPYRIGHT, TERMS, DISCLAIMER) */}
        {activeTab === 'legal' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-serif font-bold text-[#0B3B60]">
                  Legal Content Management
                </h2>
                <p className="text-xs text-gray-500 mt-1">
                  Edit live text and policies for Privacy Policy, Copyright, Terms of Service, and Medical Disclaimer.
                </p>
              </div>

              {/* Sub-tab Selector */}
              <div className="flex flex-wrap items-center bg-gray-100 p-1 rounded-xl gap-1">
                <button
                  type="button"
                  onClick={() => setActiveLegalTab('privacy')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                    activeLegalTab === 'privacy'
                      ? 'bg-white text-[#03478E] shadow-2xs'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Privacy Policy
                </button>
                <button
                  type="button"
                  onClick={() => setActiveLegalTab('copyright')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                    activeLegalTab === 'copyright'
                      ? 'bg-white text-[#03478E] shadow-2xs'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Copyright Policy
                </button>
                <button
                  type="button"
                  onClick={() => setActiveLegalTab('terms')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                    activeLegalTab === 'terms'
                      ? 'bg-white text-[#03478E] shadow-2xs'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Terms of Service
                </button>
                <button
                  type="button"
                  onClick={() => setActiveLegalTab('disclaimer')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                    activeLegalTab === 'disclaimer'
                      ? 'bg-white text-[#03478E] shadow-2xs'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Disclaimer
                </button>
              </div>
            </div>

            {/* Notifications */}
            {miscError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-xs flex items-center gap-2">
                <FaExclamationCircle className="text-base flex-shrink-0" />
                <span>{miscError}</span>
              </div>
            )}
            {miscSuccess && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-xs flex items-center gap-2">
                <FaCheckCircle className="text-base flex-shrink-0" />
                <span>{miscSuccess}</span>
              </div>
            )}

            <form onSubmit={handleMiscSave} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 space-y-6">
              
              {/* Privacy Policy Editor */}
              {activeLegalTab === 'privacy' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between border-b pb-3">
                    <div>
                      <h3 className="text-sm font-bold text-[#03478E] uppercase tracking-wide">
                        Privacy Policy
                      </h3>
                      <p className="text-xs text-gray-500">
                        Governs data handling, reader privacy, and researcher contact information.
                      </p>
                    </div>
                    <a
                      href="/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-bold text-[#03478E] hover:underline inline-flex items-center gap-1"
                    >
                      <span>View Live Page</span>
                      <FaExternalLinkAlt className="text-[10px]" />
                    </a>
                  </div>

                  <textarea
                    rows={12}
                    name="privacyPolicy"
                    value={miscData.privacyPolicy || ''}
                    onChange={handleMiscChange}
                    placeholder="Enter privacy policy text or HTML..."
                    className="w-full font-mono text-xs p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                  <p className="text-[10px] text-gray-400">
                    Supports HTML tags (&lt;p&gt;, &lt;h3&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;).
                  </p>
                </div>
              )}

              {/* Copyright Policy Editor */}
              {activeLegalTab === 'copyright' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between border-b pb-3">
                    <div>
                      <h3 className="text-sm font-bold text-[#03478E] uppercase tracking-wide">
                        Copyright & Open Access Policy
                      </h3>
                      <p className="text-xs text-gray-500">
                        Creative Commons (CC BY 4.0) licensing terms, author copyright retention, and open access declarations.
                      </p>
                    </div>
                    <a
                      href="/copyright"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-bold text-[#03478E] hover:underline inline-flex items-center gap-1"
                    >
                      <span>View Live Page</span>
                      <FaExternalLinkAlt className="text-[10px]" />
                    </a>
                  </div>

                  <textarea
                    rows={12}
                    name="copyright"
                    value={miscData.copyright || ''}
                    onChange={handleMiscChange}
                    placeholder="Enter copyright policy text or HTML..."
                    className="w-full font-mono text-xs p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>
              )}

              {/* Terms of Service Editor */}
              {activeLegalTab === 'terms' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between border-b pb-3">
                    <div>
                      <h3 className="text-sm font-bold text-[#03478E] uppercase tracking-wide">
                        Terms of Service
                      </h3>
                      <p className="text-xs text-gray-500">
                        Rules, permitted use, and portal conditions for authors and visitors.
                      </p>
                    </div>
                    <a
                      href="/terms-of-service"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-bold text-[#03478E] hover:underline inline-flex items-center gap-1"
                    >
                      <span>View Live Page</span>
                      <FaExternalLinkAlt className="text-[10px]" />
                    </a>
                  </div>

                  <textarea
                    rows={12}
                    name="termsOfService"
                    value={miscData.termsOfService || ''}
                    onChange={handleMiscChange}
                    placeholder="Enter terms of service text or HTML..."
                    className="w-full font-mono text-xs p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>
              )}

              {/* Disclaimer Editor */}
              {activeLegalTab === 'disclaimer' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between border-b pb-3">
                    <div>
                      <h3 className="text-sm font-bold text-[#03478E] uppercase tracking-wide">
                        Medical & Publication Disclaimer
                      </h3>
                      <p className="text-xs text-gray-500">
                        Clinical non-liability statement, educational disclaimer, and medical practitioner notices.
                      </p>
                    </div>
                    <a
                      href="/disclaimer"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-bold text-[#03478E] hover:underline inline-flex items-center gap-1"
                    >
                      <span>View Live Page</span>
                      <FaExternalLinkAlt className="text-[10px]" />
                    </a>
                  </div>

                  <textarea
                    rows={12}
                    name="disclaimer"
                    value={miscData.disclaimer || ''}
                    onChange={handleMiscChange}
                    placeholder="Enter medical disclaimer text or HTML..."
                    className="w-full font-mono text-xs p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>
              )}

              <div className="pt-4 border-t border-gray-100 flex justify-end">
                <button
                  type="submit"
                  disabled={miscLoading}
                  className="px-6 py-2.5 bg-[#03478E] hover:bg-[#023366] text-white rounded-xl text-xs font-bold shadow-md transition disabled:opacity-50"
                >
                  {miscLoading ? 'Saving...' : 'Save Legal Content to Live Site'}
                </button>
              </div>

            </form>
          </div>
        )}

        {/* TAB 7: SITE SETTINGS (BRANDING & HERO) */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h2 className="text-xl font-serif font-bold text-[#0B3B60]">
                Site Settings & Hero Branding
              </h2>
              <p className="text-xs text-gray-500 mt-1">
                Customize homepage hero titles, taglines, banner artwork, and core research mission statements.
              </p>
            </div>

            {/* Notifications */}
            {miscError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-xs flex items-center gap-2">
                <FaExclamationCircle className="text-base flex-shrink-0" />
                <span>{miscError}</span>
              </div>
            )}
            {miscSuccess && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-xs flex items-center gap-2">
                <FaCheckCircle className="text-base flex-shrink-0" />
                <span>{miscSuccess}</span>
              </div>
            )}

            <form onSubmit={handleMiscSave} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    Hero Banner Title
                  </label>
                  <input
                    type="text"
                    name="heroTitle"
                    value={miscData.heroTitle || ''}
                    onChange={handleMiscChange}
                    placeholder="Asian Journal of Medical and Health Sciences"
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    Hero Subtitle / Tagline
                  </label>
                  <input
                    type="text"
                    name="heroTagline"
                    value={miscData.heroTagline || ''}
                    onChange={handleMiscChange}
                    placeholder="Advancing Peer-Reviewed Clinical & Evidence-Based Research"
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>

                <div className="md:col-span-2">
                  <ImageUploadField
                    label="Hero Background Image"
                    name="heroImg"
                    value={miscData.heroImg}
                    onChange={(url) => setMiscData((prev) => ({ ...prev, heroImg: url }))}
                    placeholder="https://.../hero.jpg or upload"
                  />
                </div>

                <div className="space-y-1 md:col-span-2">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    Our Mission Statement
                  </label>
                  <textarea
                    rows={3}
                    name="ourMission"
                    value={miscData.ourMission || ''}
                    onChange={handleMiscChange}
                    placeholder="Mission statement displayed on website..."
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>

                <div className="space-y-1 md:col-span-2">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    Featured Topics / Research Focus Areas
                  </label>
                  <textarea
                    rows={3}
                    name="Topics"
                    value={miscData.Topics || ''}
                    onChange={handleMiscChange}
                    placeholder="Clinical Homoeopathy, Evidence-Based Medicine, Pharmacology..."
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>

                <div className="space-y-1 md:col-span-2">
                  <label className="block text-xs font-bold text-[#0B3B60] uppercase tracking-wider">
                    Submission Requirements Checklist
                  </label>
                  <p className="text-[11px] text-gray-500">
                    Shown on the Submit Manuscript page. One item per line.
                  </p>
                  <textarea
                    rows={7}
                    name="submissionChecklist"
                    value={miscData.submissionChecklist || ''}
                    onChange={handleMiscChange}
                    className="w-full text-xs sm:text-sm p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#03478E]/30"
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-gray-100 flex justify-end">
                <button
                  type="submit"
                  disabled={miscLoading}
                  className="px-6 py-2.5 bg-[#03478E] hover:bg-[#023366] text-white rounded-xl text-xs font-bold shadow-md transition disabled:opacity-50"
                >
                  {miscLoading ? 'Saving...' : 'Save Site Settings'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* TAB 8: ANALYTICS DASHBOARD */}
        {activeTab === 'analytics' && <AnalyticsDashboard />}

      </main>

    </div>
  );
};

export default Admin;
